import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Separator } from './ui/separator';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { 
  Layers, 
  Home,
  Flame,
  Droplet,
  Square,
  Shield,
  Trees,
  Warehouse,
  PlusCircle, 
  CheckCircle2,
  AlertTriangle,
  Wrench,
  Sparkles,
  Camera,
  MapPin,
  Loader2,
  ChevronRight,
  ChevronDown,
  ListChecks,
  Zap,
  Grid3X3
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Building Components with their specific options
const BUILDING_COMPONENTS = {
  roof: {
    name: 'Roof',
    icon: Home,
    color: 'bg-blue-500',
    subtypes: [
      { value: 'hipped', label: 'Hipped' },
      { value: 'gabled', label: 'Gabled' },
      { value: 'flat', label: 'Flat' },
      { value: 'mansard', label: 'Mansard' },
      { value: 'combination', label: 'Combination' }
    ],
    materials: [
      { value: 'asphalt_shingle', label: 'Asphalt Shingle' },
      { value: 'clay_tile', label: 'Clay Tile' },
      { value: 'concrete_tile', label: 'Concrete Tile' },
      { value: 'slate', label: 'Slate' },
      { value: 'metal', label: 'Metal' },
      { value: 'felt', label: 'Felt/Bitumen' }
    ],
    issues: [
      { value: 'missing_tile', label: 'Missing Tile/Slate', severity: 'yellow' },
      { value: 'cracked_tile', label: 'Cracked Tile', severity: 'yellow' },
      { value: 'ridge_damage', label: 'Ridge Damage', severity: 'orange' },
      { value: 'valley_leak', label: 'Valley Leak', severity: 'red' },
      { value: 'moss_growth', label: 'Moss/Algae Growth', severity: 'green' },
      { value: 'sagging', label: 'Sagging Structure', severity: 'red' },
      { value: 'flashing_failed', label: 'Flashing Failed', severity: 'orange' }
    ]
  },
  chimneys: {
    name: 'Chimneys',
    icon: Flame,
    color: 'bg-orange-500',
    subtypes: [
      { value: 'brick', label: 'Brick' },
      { value: 'stone', label: 'Stone' },
      { value: 'rendered', label: 'Rendered' }
    ],
    materials: [
      { value: 'brick', label: 'Brick' },
      { value: 'stone', label: 'Stone' },
      { value: 'concrete', label: 'Concrete' }
    ],
    issues: [
      { value: 'pointing_failed', label: 'Pointing Failed', severity: 'yellow' },
      { value: 'leaning', label: 'Leaning Stack', severity: 'red' },
      { value: 'cap_missing', label: 'Cap/Cowl Missing', severity: 'yellow' },
      { value: 'flashing_failed', label: 'Flashing Failed', severity: 'orange' },
      { value: 'spalling', label: 'Spalling Brickwork', severity: 'orange' },
      { value: 'pot_damaged', label: 'Pot Damaged', severity: 'yellow' }
    ]
  },
  gutters: {
    name: 'Gutters & RWG',
    icon: Droplet,
    color: 'bg-cyan-500',
    subtypes: [
      { value: 'upvc', label: 'uPVC' },
      { value: 'cast_iron', label: 'Cast Iron' },
      { value: 'aluminium', label: 'Aluminium' },
      { value: 'copper', label: 'Copper' }
    ],
    materials: [
      { value: 'upvc', label: 'uPVC' },
      { value: 'cast_iron', label: 'Cast Iron' },
      { value: 'aluminium', label: 'Aluminium' }
    ],
    issues: [
      { value: 'blocked', label: 'Blocked', severity: 'yellow' },
      { value: 'leaking_joint', label: 'Leaking Joint', severity: 'yellow' },
      { value: 'sagging', label: 'Sagging', severity: 'yellow' },
      { value: 'rust_perforation', label: 'Rust/Perforation', severity: 'orange' },
      { value: 'missing_section', label: 'Missing Section', severity: 'orange' },
      { value: 'bracket_failed', label: 'Bracket Failed', severity: 'yellow' }
    ]
  },
  walls: {
    name: 'External Walls',
    icon: Grid3X3,
    color: 'bg-amber-600',
    subtypes: [
      { value: 'cavity_brick', label: 'Cavity Brick' },
      { value: 'solid_brick', label: 'Solid Brick' },
      { value: 'stone', label: 'Stone' },
      { value: 'rendered', label: 'Rendered' },
      { value: 'timber_frame', label: 'Timber Frame' }
    ],
    materials: [
      { value: 'brick', label: 'Brick' },
      { value: 'stone', label: 'Stone' },
      { value: 'render', label: 'Render' },
      { value: 'cladding', label: 'Cladding' }
    ],
    issues: [
      { value: 'cracking', label: 'Cracking', severity: 'orange' },
      { value: 'spalling', label: 'Spalling Brick', severity: 'yellow' },
      { value: 'pointing_failed', label: 'Pointing Failed', severity: 'yellow' },
      { value: 'damp_penetrating', label: 'Penetrating Damp', severity: 'orange' },
      { value: 'render_blown', label: 'Blown Render', severity: 'yellow' },
      { value: 'staining', label: 'Staining', severity: 'green' },
      { value: 'tie_failure', label: 'Wall Tie Failure', severity: 'red' }
    ]
  },
  windows: {
    name: 'Windows & Doors',
    icon: Square,
    color: 'bg-purple-500',
    subtypes: [
      { value: 'upvc', label: 'uPVC' },
      { value: 'timber', label: 'Timber' },
      { value: 'aluminium', label: 'Aluminium' },
      { value: 'composite', label: 'Composite' }
    ],
    materials: [
      { value: 'upvc', label: 'uPVC' },
      { value: 'timber', label: 'Timber' },
      { value: 'aluminium', label: 'Aluminium' }
    ],
    issues: [
      { value: 'blown_unit', label: 'Blown Double Glazing', severity: 'yellow' },
      { value: 'rot', label: 'Rot (Timber)', severity: 'orange' },
      { value: 'seal_failed', label: 'Seal Failed', severity: 'yellow' },
      { value: 'hinge_dropped', label: 'Hinge Dropped', severity: 'yellow' },
      { value: 'lock_failed', label: 'Lock Failed', severity: 'yellow' },
      { value: 'cracked_glass', label: 'Cracked Glass', severity: 'orange' },
      { value: 'draughty', label: 'Draughty', severity: 'green' }
    ]
  },
  dpc: {
    name: 'DPC / Damp',
    icon: Shield,
    color: 'bg-teal-500',
    subtypes: [
      { value: 'visible', label: 'Visible DPC' },
      { value: 'bridged', label: 'Bridged DPC' },
      { value: 'none', label: 'No DPC' }
    ],
    materials: [
      { value: 'bitumen', label: 'Bitumen' },
      { value: 'slate', label: 'Slate' },
      { value: 'plastic', label: 'Plastic' },
      { value: 'injected', label: 'Chemical Injection' }
    ],
    issues: [
      { value: 'bridged', label: 'DPC Bridged', severity: 'orange' },
      { value: 'rising_damp', label: 'Rising Damp', severity: 'red' },
      { value: 'ground_high', label: 'High Ground Level', severity: 'yellow' },
      { value: 'render_bridge', label: 'Render Bridging', severity: 'orange' }
    ]
  },
  external: {
    name: 'External Areas',
    icon: Trees,
    color: 'bg-green-500',
    subtypes: [
      { value: 'paving', label: 'Paving' },
      { value: 'decking', label: 'Decking' },
      { value: 'driveway', label: 'Driveway' },
      { value: 'garden', label: 'Garden' }
    ],
    materials: [
      { value: 'concrete', label: 'Concrete' },
      { value: 'block_paving', label: 'Block Paving' },
      { value: 'timber', label: 'Timber' },
      { value: 'gravel', label: 'Gravel' }
    ],
    issues: [
      { value: 'trip_hazard', label: 'Trip Hazard', severity: 'orange' },
      { value: 'poor_drainage', label: 'Poor Drainage', severity: 'yellow' },
      { value: 'cracked', label: 'Cracked/Broken', severity: 'yellow' },
      { value: 'rot', label: 'Rot (Decking)', severity: 'orange' },
      { value: 'subsidence', label: 'Subsidence', severity: 'red' }
    ]
  },
  outbuildings: {
    name: 'Outbuildings',
    icon: Warehouse,
    color: 'bg-gray-500',
    subtypes: [
      { value: 'garage', label: 'Garage' },
      { value: 'shed', label: 'Shed' },
      { value: 'conservatory', label: 'Conservatory' },
      { value: 'extension', label: 'Extension' }
    ],
    materials: [
      { value: 'brick', label: 'Brick' },
      { value: 'timber', label: 'Timber' },
      { value: 'upvc', label: 'uPVC' },
      { value: 'metal', label: 'Metal' }
    ],
    issues: [
      { value: 'roof_leak', label: 'Roof Leak', severity: 'orange' },
      { value: 'door_failed', label: 'Door Failed', severity: 'yellow' },
      { value: 'rot', label: 'Rot', severity: 'orange' },
      { value: 'damp', label: 'Damp', severity: 'yellow' },
      { value: 'structural', label: 'Structural Issue', severity: 'red' }
    ]
  }
};

// Severity colors
const SEVERITY_COLORS = {
  green: { bg: 'bg-green-500', text: 'text-green-700', label: 'Minor' },
  yellow: { bg: 'bg-yellow-500', text: 'text-yellow-700', label: 'Moderate' },
  orange: { bg: 'bg-orange-500', text: 'text-orange-700', label: 'Significant' },
  red: { bg: 'bg-red-500', text: 'text-red-700', label: 'Urgent' }
};

// Action Required Options
const ACTION_OPTIONS = [
  { value: 'monitor', label: 'Monitor', color: 'bg-blue-500' },
  { value: 'repair', label: 'Repair', color: 'bg-yellow-500' },
  { value: 'replace', label: 'Replace', color: 'bg-orange-500' },
  { value: 'clean', label: 'Clean', color: 'bg-green-500' },
  { value: 'immediate', label: 'Immediate Action', color: 'bg-red-500' }
];

export default function FixCardInspectionDialog({ open, onClose, inspection, onSuccess }) {
  const { token } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('identity');
  const [loading, setLoading] = useState(false);
  
  // Selected component for configuration
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [expandedComponents, setExpandedComponents] = useState(new Set(['roof']));
  
  // Asset Identity per component
  const [componentConfigs, setComponentConfigs] = useState({});
  
  // Findings
  const [findings, setFindings] = useState([]);
  
  // New finding form
  const [newFinding, setNewFinding] = useState({
    component: '',
    issue: '',
    location: '',
    notes: '',
    action: 'monitor',
    severity: 'yellow'
  });

  // Toggle component expansion
  const toggleComponent = (key) => {
    setExpandedComponents(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  // Update component config
  const updateConfig = (componentKey, field, value) => {
    setComponentConfigs(prev => ({
      ...prev,
      [componentKey]: {
        ...prev[componentKey],
        [field]: value
      }
    }));
  };

  // Add finding
  const addFinding = (componentKey, issueValue) => {
    const component = BUILDING_COMPONENTS[componentKey];
    const issue = component.issues.find(i => i.value === issueValue);
    
    if (!issue) return;
    
    const finding = {
      id: `F-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      component: componentKey,
      componentName: component.name,
      issue: issue.value,
      issueLabel: issue.label,
      severity: issue.severity,
      location: '',
      notes: '',
      action: issue.severity === 'red' ? 'immediate' : issue.severity === 'orange' ? 'repair' : 'monitor',
      photos: [],
      config: componentConfigs[componentKey] || {}
    };
    
    setFindings(prev => [...prev, finding]);
    toast.success(`Added: ${component.name} - ${issue.label}`);
  };

  // Remove finding
  const removeFinding = (findingId) => {
    setFindings(prev => prev.filter(f => f.id !== findingId));
  };

  // Update finding
  const updateFinding = (findingId, field, value) => {
    setFindings(prev => prev.map(f => 
      f.id === findingId ? { ...f, [field]: value } : f
    ));
  };

  // Save findings
  const saveFindings = async () => {
    if (findings.length === 0) {
      toast.error('No findings to save');
      return;
    }
    
    setLoading(true);
    try {
      // In a real implementation, save to backend
      onSuccess?.({
        component_configs: componentConfigs,
        findings: findings
      });
      toast.success(`Saved ${findings.length} findings`);
      onClose();
    } catch (error) {
      toast.error('Failed to save findings');
    } finally {
      setLoading(false);
    }
  };

  // Count findings per component
  const getComponentFindingCount = (key) => {
    return findings.filter(f => f.component === key).length;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[95vh] flex flex-col" data-testid="fix-card-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-blue-500" />
            Fix Card Inspection System
            {findings.length > 0 && (
              <Badge variant="secondary" className="ml-2">{findings.length} findings</Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {inspection?.property_address}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="identity" className="flex items-center gap-2" data-testid="identity-tab">
              <Home className="h-4 w-4" />
              Asset Identity
            </TabsTrigger>
            <TabsTrigger value="fixcards" className="flex items-center gap-2" data-testid="fixcards-tab">
              <ListChecks className="h-4 w-4" />
              Fix Cards
              {findings.length > 0 && (
                <Badge variant="secondary" className="ml-1">{findings.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="summary" className="flex items-center gap-2" data-testid="summary-tab">
              <CheckCircle2 className="h-4 w-4" />
              Summary
            </TabsTrigger>
          </TabsList>

          {/* Asset Identity Tab */}
          <TabsContent value="identity" className="flex-1 overflow-y-auto mt-4">
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground mb-4">
                Configure each building component to determine which fix cards are available.
              </p>
              
              {Object.entries(BUILDING_COMPONENTS).map(([key, component]) => {
                const IconComponent = component.icon;
                const isExpanded = expandedComponents.has(key);
                const findingCount = getComponentFindingCount(key);
                const config = componentConfigs[key] || {};
                
                return (
                  <Card key={key} className="overflow-hidden">
                    <div 
                      className={`flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 transition-colors ${isExpanded ? 'border-b' : ''}`}
                      onClick={() => toggleComponent(key)}
                      data-testid={`component-${key}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${component.color} text-white`}>
                          <IconComponent className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="font-medium">{component.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {config.subtype ? component.subtypes.find(s => s.value === config.subtype)?.label : 'Not configured'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {findingCount > 0 && (
                          <Badge variant="secondary">{findingCount}</Badge>
                        )}
                        {isExpanded ? (
                          <ChevronDown className="h-5 w-5 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>
                    </div>
                    
                    {isExpanded && (
                      <CardContent className="pt-4">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          <div>
                            <Label className="text-xs">Type</Label>
                            <Select
                              value={config.subtype || ''}
                              onValueChange={(v) => updateConfig(key, 'subtype', v)}
                            >
                              <SelectTrigger className="mt-1" data-testid={`${key}-subtype`}>
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                              <SelectContent>
                                {component.subtypes.map(st => (
                                  <SelectItem key={st.value} value={st.value}>{st.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div>
                            <Label className="text-xs">Material</Label>
                            <Select
                              value={config.material || ''}
                              onValueChange={(v) => updateConfig(key, 'material', v)}
                            >
                              <SelectTrigger className="mt-1" data-testid={`${key}-material`}>
                                <SelectValue placeholder="Select material" />
                              </SelectTrigger>
                              <SelectContent>
                                {component.materials.map(m => (
                                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div>
                            <Label className="text-xs">Age (years)</Label>
                            <Input
                              type="number"
                              placeholder="e.g., 15"
                              value={config.age || ''}
                              onChange={(e) => updateConfig(key, 'age', e.target.value)}
                              className="mt-1"
                              data-testid={`${key}-age`}
                            />
                          </div>
                        </div>
                        
                        {/* Quick Add Issues */}
                        <div className="mt-4">
                          <Label className="text-xs mb-2 block">Quick Add Issues</Label>
                          <div className="flex flex-wrap gap-2">
                            {component.issues.map(issue => {
                              const sevColor = SEVERITY_COLORS[issue.severity];
                              return (
                                <Button
                                  key={issue.value}
                                  variant="outline"
                                  size="sm"
                                  className={`text-xs border-2 hover:${sevColor.bg} hover:text-white`}
                                  style={{ borderColor: `var(--${issue.severity}-500, #EAB308)` }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    addFinding(key, issue.value);
                                  }}
                                  data-testid={`add-${key}-${issue.value}`}
                                >
                                  <PlusCircle className="h-3 w-3 mr-1" />
                                  {issue.label}
                                </Button>
                              );
                            })}
                          </div>
                        </div>
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Fix Cards Tab */}
          <TabsContent value="fixcards" className="flex-1 overflow-y-auto mt-4">
            {findings.length === 0 ? (
              <div className="text-center py-12">
                <ListChecks className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">No findings added yet</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Go to Asset Identity tab and click issues to add fix cards
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {findings.map((finding) => {
                  const sevColor = SEVERITY_COLORS[finding.severity];
                  const component = BUILDING_COMPONENTS[finding.component];
                  const IconComponent = component?.icon || Home;
                  
                  return (
                    <Card key={finding.id} className="overflow-hidden">
                      <div className="flex items-start gap-4 p-4">
                        <div className={`p-2 rounded-lg ${component?.color || 'bg-gray-500'} text-white flex-shrink-0`}>
                          <IconComponent className="h-5 w-5" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <p className="font-medium">{finding.componentName}</p>
                            <Badge className={sevColor.bg}>{sevColor.label}</Badge>
                          </div>
                          <p className="text-sm">{finding.issueLabel}</p>
                          
                          <div className="grid grid-cols-2 gap-3 mt-3">
                            <div>
                              <Label className="text-xs">Location</Label>
                              <Input
                                placeholder="e.g., Front elevation"
                                value={finding.location}
                                onChange={(e) => updateFinding(finding.id, 'location', e.target.value)}
                                className="mt-1 h-8 text-sm"
                              />
                            </div>
                            <div>
                              <Label className="text-xs">Action</Label>
                              <Select
                                value={finding.action}
                                onValueChange={(v) => updateFinding(finding.id, 'action', v)}
                              >
                                <SelectTrigger className="mt-1 h-8 text-sm">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {ACTION_OPTIONS.map(opt => (
                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          
                          <div className="mt-2">
                            <Label className="text-xs">Notes</Label>
                            <Textarea
                              placeholder="Additional notes..."
                              value={finding.notes}
                              onChange={(e) => updateFinding(finding.id, 'notes', e.target.value)}
                              className="mt-1 h-16 text-sm resize-none"
                            />
                          </div>
                        </div>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive flex-shrink-0"
                          onClick={() => removeFinding(finding.id)}
                        >
                          Remove
                        </Button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Summary Tab */}
          <TabsContent value="summary" className="flex-1 overflow-y-auto mt-4">
            <div className="space-y-6">
              <div className="grid grid-cols-4 gap-4">
                {Object.entries(SEVERITY_COLORS).map(([sev, config]) => {
                  const count = findings.filter(f => f.severity === sev).length;
                  return (
                    <Card key={sev}>
                      <CardContent className="p-4 text-center">
                        <div className={`w-8 h-8 rounded-full ${config.bg} mx-auto mb-2`} />
                        <p className="text-2xl font-bold">{count}</p>
                        <p className="text-xs text-muted-foreground">{config.label}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              
              <Separator />
              
              <div>
                <h4 className="font-medium mb-3">Findings by Component</h4>
                <div className="space-y-2">
                  {Object.entries(BUILDING_COMPONENTS).map(([key, component]) => {
                    const componentFindings = findings.filter(f => f.component === key);
                    if (componentFindings.length === 0) return null;
                    
                    const IconComponent = component.icon;
                    return (
                      <div key={key} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className={`p-1.5 rounded ${component.color} text-white`}>
                            <IconComponent className="h-4 w-4" />
                          </div>
                          <span className="font-medium">{component.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {Object.entries(SEVERITY_COLORS).map(([sev, config]) => {
                            const count = componentFindings.filter(f => f.severity === sev).length;
                            if (count === 0) return null;
                            return (
                              <Badge key={sev} className={config.bg}>{count}</Badge>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Footer */}
        <div className="flex justify-between items-center pt-4 border-t mt-4">
          <div className="flex items-center gap-4">
            {Object.entries(SEVERITY_COLORS).map(([sev, config]) => {
              const count = findings.filter(f => f.severity === sev).length;
              return (
                <div key={sev} className="flex items-center gap-1">
                  <div className={`w-3 h-3 rounded-full ${config.bg}`} />
                  <span className="text-sm">{count}</span>
                </div>
              );
            })}
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} data-testid="cancel-btn">
              Cancel
            </Button>
            <Button 
              onClick={saveFindings}
              disabled={findings.length === 0 || loading}
              data-testid="save-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save {findings.length} Findings
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
